import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router";
import { Box, Typography } from "@mui/material";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { useSnackbar } from "notistack";

import { useBreadcrumb } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import { useAbility } from "../../../hooks/permissions/useAbility.jsx";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import Spinner from "@accredo/fe-shared/components/Spinner.jsx";
import CustomerDetailsCard from "./components/CustomerDetailsCard.jsx";
import CustomerContractsTable from "./components/CustomerContractsTable.jsx";
import CustomerObjectsTable from "./components/CustomerObjectsTable.jsx";
import CustomerClaimsTable from "./components/CustomerClaimsTable.jsx";
import BankAccountTable from "../../../components/tables/BankAccountTable.jsx";
import CustomerUsersTable from "./components/CustomerUsersTable.jsx";
import CustomerSettingsPanel from "./components/CustomerSettingsPanel.jsx";

// Unterroute -> Tab-Wert, je Kunde unter /admin/kunden/:customerId/<segment>
const TAB_SEGMENTS = {
  vertraege: "2",
  objekte: "3",
  schaeden: "4",
  bankaccounts: "5",
  benutzer: "6",
  einstellungen: "7",
};

export default function CustomerShowPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();
  const { can } = useAbility();
  // Verträge/Objekte sind - wie in der Sidebar (ContractPolicy/ObjectPolicy) -
  // aktuell nur für sysadmin sichtbar.
  const canViewContracts = can("Contract", "viewAny");
  const canViewObjects = can("Object", "viewAny");
  // Bankkonten-Tab ist bewusst enger als die übrige Kundenansicht - nur sysadmin (ACC-323).
  const canViewBankAccounts = can("BankAccount", "viewAny");

  const [customer, setCustomer] = useState(null);
  // BE-berechnet (KundeResource::can_update, CustomerPolicy::update) statt einer
  // eigenen FE-Policy-Regel - siehe docs/architecture/frontend-patterns.md.
  const canViewSettings = !!customer?.can_update;
  const [isLoading, setIsLoading] = useState(true);
  const [tabValue, setTabValue] = useState("1");

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      await backendGet(`data/kundes/${customerId}`, {
        label: "Kundendaten",
        enqueueSnackbar,
        setIsLoading,
        setResult: (res) => {
          setCustomer(res.customer || res.data);
        },
      });
    } catch (error) {
      enqueueSnackbar("Kundendaten konnten nicht geladen werden.", {
        variant: "error",
      });
      // eslint-disable-next-line no-console
      console.error("Failed to fetch customer data", error);
    } finally {
      setIsLoading(false);
    }
  }, [customerId, enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const segment = location.pathname.split("/").pop();
    setTabValue(TAB_SEGMENTS[segment] || "1");
  }, [location.pathname]);

  const handleTabChange = (event, newValue) => {
    const segment = Object.keys(TAB_SEGMENTS).find(
      (key) => TAB_SEGMENTS[key] === newValue
    );
    navigate(
      segment
        ? `/admin/kunden/${customerId}/${segment}`
        : `/admin/kunden/${customerId}`
    );
  };

  const customerDisplayName = customer
    ? customer.display_name || `Kunde #${customerId}`
    : `Kunde #${customerId}`;

  useEffect(() => {
    setBreadcrumbs([
      { name: "Home", path: "/home" },
      { name: "Kunden", path: "/admin/kunden" },
      {
        name: customerDisplayName,
        path: `/admin/kunden/${customerId}`,
      },
    ]);
  }, [setBreadcrumbs, customerDisplayName, customerId]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!customer) {
    return (
      <Box>
        <Typography variant="h1" gutterBottom>
          Kundendetails
        </Typography>
        <Typography variant="h3" sx={{ mb: 3 }}>
          Kunde nicht gefunden
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h1" gutterBottom>
          {customerDisplayName}
        </Typography>
      </Box>

      <Box sx={{ width: "100%", typography: "body1" }}>
        <TabContext value={tabValue}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList onChange={handleTabChange} aria-label="Kundendetails Tabs">
              <Tab label="Übersicht" value="1" />
              {canViewContracts && <Tab label="Verträge" value="2" />}
              {canViewObjects && <Tab label="Objekte" value="3" />}
              <Tab label="Schäden" value="4" />
              {canViewBankAccounts && <Tab label="Bankkonten" value="5" />}
              <Tab label="Benutzer" value="6" />
              {canViewSettings && <Tab label="Einstellungen" value="7" />}
            </TabList>
          </Box>
          <TabPanel value="1" sx={{ paddingX: 0 }}>
            <CustomerDetailsCard customer={customer} />
          </TabPanel>
          {canViewContracts && (
            <TabPanel value="2" sx={{ paddingX: 0 }}>
              <CustomerContractsTable
                customerId={customerId}
                objectRefNumLabel={customer.object_ref_num_label}
              />
            </TabPanel>
          )}
          {canViewObjects && (
            <TabPanel value="3" sx={{ paddingX: 0 }}>
              <CustomerObjectsTable
                customerId={customerId}
                objectRefNumLabel={customer.object_ref_num_label}
              />
            </TabPanel>
          )}
          <TabPanel value="4" sx={{ paddingX: 0 }}>
            <CustomerClaimsTable customerId={customerId} />
          </TabPanel>
          {canViewBankAccounts && (
            <TabPanel value="5" sx={{ paddingX: 0 }}>
              <BankAccountTable scope="customer" customerId={customerId} />
            </TabPanel>
          )}
          <TabPanel value="6" sx={{ paddingX: 0 }}>
            <CustomerUsersTable customerId={customerId} />
          </TabPanel>
          {canViewSettings && (
            <TabPanel value="7" sx={{ paddingX: 0 }}>
              <CustomerSettingsPanel
                customerId={customerId}
                customer={customer}
                onUpdate={fetchData}
              />
            </TabPanel>
          )}
        </TabContext>
      </Box>
    </Box>
  );
}
